import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import {
  fetchProfile,
  getSession,
  onAuthStateChange,
  signOut,
  updateProfile,
  type Profile,
} from "./lib/auth";
import { fetchMyHouseholds } from "./lib/households";
import SignInScreen from "./screens/SignInScreen";
import ProfileSetupScreen from "./screens/ProfileSetupScreen";
import HomeSetupScreen from "./screens/HomeSetupScreen";
import HomeScreen from "./screens/HomeScreen";
import AddPlantScreen from "./screens/AddPlantScreen";
import PlantDetailScreen from "./screens/PlantDetailScreen";
import EditPlantScreen from "./screens/EditPlantScreen";
import EditProfileScreen from "./screens/EditProfileScreen";
import SettingsScreen from "./screens/SettingsScreen";
import HousematesScreen from "./screens/HousematesScreen";

type Stage =
  | "loading"
  | "signed_out"
  | "needs_profile"
  | "needs_household"
  | "ready";

export default function App() {
  const [stage, setStage] = useState<Stage>("loading");
  const [session, setSession] = useState<Session | null>(null);
  const [_profile, setProfile] = useState<Profile | null>(null);
  const location = useLocation();

  useEffect(() => {
    getSession().then(setSession);
    const sub = onAuthStateChange((s) => setSession(s));
    return () => sub.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function evaluate() {
      if (!session) {
        if (!cancelled) {
          setProfile(null);
          setStage("signed_out");
        }
        return;
      }
      if (!cancelled) setStage("loading");
      try {
        const email = session.user.email;
        if (!email) {
          await signOut();
          return;
        }
        let p = await fetchProfile(session.user.id);
        if (cancelled) return;

        // Backfill avatar from Google metadata if missing
        if (p && !p.avatar_url) {
          const googleAvatar =
            (session.user.user_metadata?.avatar_url as string) ||
            (session.user.user_metadata?.picture as string) ||
            null;
          if (googleAvatar) {
            try {
              p = await updateProfile(session.user.id, {
                avatar_url: googleAvatar,
              });
            } catch (err) {
              console.warn("avatar backfill failed:", err);
            }
          }
        }

        setProfile(p);
        if (!p) {
          setStage("needs_profile");
          return;
        }
        const households = await fetchMyHouseholds();
        if (cancelled) return;
        if (households.length === 0) {
          setStage("needs_household");
        } else {
          setStage("ready");
        }
      } catch (err) {
        console.error("auth evaluation failed:", err);
        if (!cancelled) setStage("signed_out");
      }
    }
    evaluate();
    return () => {
      cancelled = true;
    };
  }, [session]);

  // OAuth callback — Supabase's PKCE flow exchanges the ?code= param for a
  // session and fires onAuthStateChange. Show a loading screen while that
  // resolves, then redirect to / once we have a session.
  if (location.pathname === "/auth/callback") {
    if (session) {
      return <Navigate to="/" replace />;
    }
    return <div className="loading-screen">Signing you in…</div>;
  }

  if (stage === "loading") {
    return <div className="loading-screen">Loading…</div>;
  }

  if (stage === "signed_out") {
    return <SignInScreen />;
  }

  if (stage === "needs_profile" && session) {
    return (
      <ProfileSetupScreen
        userId={session.user.id}
        defaultDisplayName={
          (session.user.user_metadata?.full_name as string) ||
          (session.user.user_metadata?.name as string) ||
          ""
        }
        avatarUrl={
          (session.user.user_metadata?.avatar_url as string) ||
          (session.user.user_metadata?.picture as string) ||
          null
        }
        onDone={() => setSession((s) => (s ? { ...s } : s))}
      />
    );
  }

  if (stage === "needs_household") {
    return (
      <HomeSetupScreen
        onDone={() => setSession((s) => (s ? { ...s } : s))}
      />
    );
  }

  // ready
  return (
    <Routes>
      <Route path="/" element={<HomeScreen />} />
      <Route path="/add-plant" element={<AddPlantScreen />} />
      <Route path="/plant/:plantId" element={<PlantDetailScreen />} />
      <Route path="/plant/:plantId/edit" element={<EditPlantScreen />} />
      <Route path="/settings" element={<SettingsScreen />} />
      <Route path="/settings/profile" element={<EditProfileScreen />} />
      <Route path="/housemates" element={<HousematesScreen />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
